import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";

const PAGE_SIZE = 10;

export default function ClientProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const loadLookups = useCallback(async () => {
    try {
      const res = await apiFetch("/api/client-portal/accounts");
      if (res.ok) setAccounts(await res.json());
    } catch {
      setAccounts([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (accountId) params.set("accountId", accountId);
      const res = await apiFetch(`/api/client-portal/projects?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load client projects.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [accountId]);

  const totalPages = Math.max(1, Math.ceil(projects.length / PAGE_SIZE));
  const pagedProjects = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return projects.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [page, projects, totalPages]);

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Client Projects"
        subtitle={`${projects.length} linked project${projects.length === 1 ? "" : "s"} in the current view.`}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <select value={accountId} onChange={(event) => setAccountId(event.target.value)} aria-label="Filter by client">
            <option value="">All clients</option>
            {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card client-portal-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4].map((item) => <Loader key={item} type="skeleton" height={58} style={{ marginBottom: 10 }} />)}
          </div>
        ) : projects.length === 0 ? (
          <EmptyState
            title="No client projects linked"
            subtitle="Link a client account to a project to show delivery progress here."
          />
        ) : (
          <>
            <div className="client-portal-project-grid">
              {pagedProjects.map((project) => (
                <article key={`${project.clientAccountId}-${project.projectId}`} className="client-portal-project-card">
                  <div className="client-portal-panel-header">
                    <div>
                      <h3>{project.projectName}</h3>
                      <p>{project.clientName}</p>
                    </div>
                    <span className="client-portal-percent">{project.progressPercent}%</span>
                  </div>
                  <div className="client-portal-progress">
                    <span style={{ width: `${project.progressPercent || 0}%` }} />
                  </div>
                  <div className="client-portal-project-stats">
                    <div>
                      <span>Total</span>
                      <strong>{project.totalTasks}</strong>
                    </div>
                    <div>
                      <span>Open</span>
                      <strong>{project.openTasks}</strong>
                    </div>
                    <div>
                      <span>Done</span>
                      <strong>{project.completedTasks}</strong>
                    </div>
                    <div>
                      <span>Overdue</span>
                      <strong>{project.overdueTasks}</strong>
                    </div>
                  </div>
                  <div className="client-portal-card-foot">
                    <span>Next due</span>
                    <strong>{project.nextDueDate ? formatDate(project.nextDueDate) : "-"}</strong>
                  </div>
                </article>
              ))}
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={projects.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>
    </div>
  );
}
